import React, { useState } from 'react';
import axios from 'axios';
import { Send, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Prefill the message when a product page CTA wrote an intent to sessionStorage.
  React.useEffect(() => {
    const applyIntent = (intent) => {
      if (!intent) return;
      setFormData((prev) => ({
        ...prev,
        message: prev.message ? prev.message : `I'm interested in: ${intent}\n\n`,
      }));
    };

    // Case 1: an external page (e.g. product detail) set the intent before navigating here.
    try {
      const intent = sessionStorage.getItem('lab21g.contactIntent');
      if (intent) {
        applyIntent(intent);
        sessionStorage.removeItem('lab21g.contactIntent');
      }
    } catch (e) {
      // sessionStorage unavailable — no prefill, but the form still works.
    }

    // Case 2: the home page Services section dispatches this event when a CTA is tapped,
    // since the ContactForm is already mounted on the same page.
    const handler = (e) => {
      const intent = (e && e.detail && e.detail.intent) || '';
      applyIntent(intent);
    };
    window.addEventListener('lab21g:contact-intent', handler);
    return () => window.removeEventListener('lab21g:contact-intent', handler);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 surface-1" data-testid="contact-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="eyebrow" data-testid="contact-badge">Get In Touch</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 mb-4 text-strong" data-testid="contact-heading">
            What Can We Do For You?
          </h2>
          <p className="text-base text-muted" data-testid="contact-description">
            Excel automation, live streaming setups, training, custom widgets — tell us what you need.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="card-surface p-8 sm:p-10" data-testid="contact-form">
          {success && (
            <div className="mb-6 p-4 rounded-lg border flex items-center space-x-2" style={{ backgroundColor: 'rgba(57, 255, 20, 0.08)', borderColor: 'rgba(57, 255, 20, 0.25)' }} data-testid="contact-success-message">
              <CheckCircle className="w-5 h-5 brand-green" style={{ color: '#39FF14' }} />
              <span className="brand-green text-sm" style={{ color: '#39FF14' }}>Thank you! We'll be in touch soon.</span>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 rounded-lg border border-red-500/30 bg-red-500/5" data-testid="contact-error-message">
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-default mb-2">Your Name</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full input-field px-4 py-3 text-sm"
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-default mb-2">Your Email</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full input-field px-4 py-3 text-sm"
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-default mb-2">What can we do for you?</label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={6}
                className="w-full input-field px-4 py-3 text-sm resize-none"
                placeholder="Tell us about your project — custom Excel sheets, VBA automation, live streaming setup, training needs, sales data reports, or anything else..."
                data-testid="contact-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary px-6 py-3.5 inline-flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="contact-submit-button"
            >
              <Send className="w-4 h-4" />
              <span>{loading ? 'Sending...' : 'Send Message'}</span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
